interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  ogImage?: string;
  schemaType: "Organization" | "WebSite" | "WebPage" | "Article" | "FAQPage";
  schemaData?: Record<string, unknown>;
  articleData?: {
    headline: string;
    author: string;
    datePublished: string;
    dateModified: string;
    image: string;
  };
  faqData?: Array<{ question: string; answer: string }>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogType = "website",
  ogImage,
  schemaType,
  schemaData,
  articleData,
  faqData,
}: SEOHeadProps) {
  const buildSchema = () => {
    const base = { "@context": "https://schema.org" };

    switch (schemaType) {
      case "Organization":
        return {
          ...base,
          "@type": "Organization",
          name: (schemaData as Record<string, unknown>)?.name || "哈哈漫画网",
          url: canonicalUrl,
          description: (schemaData as Record<string, unknown>)?.description || "",
        };
      case "WebSite":
        return {
          ...base,
          "@type": "WebSite",
          name: (schemaData as Record<string, unknown>)?.name || "哈哈漫画网",
          url: canonicalUrl,
          description: (schemaData as Record<string, unknown>)?.description || "",
        };
      case "WebPage":
        return {
          ...base,
          "@type": "WebPage",
          name: title,
          description,
          url: canonicalUrl,
        };
      case "Article":
        return {
          ...base,
          "@type": "Article",
          headline: articleData?.headline || title,
          description,
          author: { "@type": "Person", name: articleData?.author || "" },
          datePublished: articleData?.datePublished || "",
          dateModified: articleData?.dateModified || articleData?.datePublished || "",
          image: articleData?.image || "",
        };
      case "FAQPage":
        return {
          ...base,
          "@type": "FAQPage",
          mainEntity: (faqData || []).map((item) => ({
            "@type": "Question",
            name: item.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: item.answer,
            },
          })),
        };
      default:
        return null;
    }
  };

  const schema = buildSchema();

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="哈哈漫画网" />
      <meta property="og:locale" content="zh_CN" />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}
      <meta name="geo.region" content="CN" />
      <meta name="geo.placename" content="Beijing" />
      <meta name="geo.position" content="39.9042;116.4074" />
      <meta httpEquiv="last-modified" content={new Date().toISOString().split("T")[0]} />
      {schema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </>
  );
}